import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faArrowLeft, faEnvelope, faEnvelopeOpen, faMousePointer, 
  faExclamationTriangle, faPaperPlane, faMobileAlt, faDesktop, 
  faGlobe, faMapMarkerAlt, faClock, faInfoCircle, faCheckCircle,
  faDownload, faPaperclip
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, PointElement, LineElement, Filler } from 'chart.js';
import { Doughnut, Bar, Line } from 'react-chartjs-2';
import "../../components/skeletonRow.css";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, PointElement, LineElement, Filler);

const EmailEventLogsReport = () => {
  const { messageId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const source = queryParams.get("source");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [metrics, setMetrics] = useState({
    total: 0, opens: 0, clicks: 0, deliveries: 0, bounces: 0, sends: 0, complaints: 0
  });

  const [deviceStats, setDeviceStats] = useState({ desktop: 0, mobile: 0, unknown: 0 });
  const [browserStats, setBrowserStats] = useState({ chrome: 0, safari: 0, firefox: 0, edge: 0, other: 0 });
  const [emailFrom, setEmailFrom] = useState(null);
  const [htmlCode, setHtmlCode] = useState("");
  const [attachmentName, setAttachmentName] = useState(null);
  const [attachmentLink, setAttachmentLink] = useState(null);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const endpoint = source === 'bravo' ? 'email-bravo/statistics' : 'reports/email-statistics';
      const response = await axios.get(`${API_URL}${endpoint}/${messageId}/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Email_Details_${messageId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download email PDF:", error);
    } finally {
      setDownloadingPdf(false);
    }
  };


  useEffect(() => {
    const fetchEventLogs = async () => {
      try {
        const endpoint = source === 'bravo' ? 'email-bravo/statistics' : 'reports/email-statistics';
        const response = await axios.get(`${API_URL}${endpoint}/${messageId}/events`);
        if (response.data && response.data.status) {
          setData(response.data.data);
          setMetrics(response.data.metrics);
          setEmailFrom(response.data.email_from || null);
          setHtmlCode(response.data.htmlContent || "");
          setAttachmentName(response.data.attachmentName || null);
          setAttachmentLink(response.data.attachmentLink || null);
          analyzeUserAgents(response.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch email event logs:", error);
      } finally {
        setLoading(false);
      }
    };
    if (messageId) fetchEventLogs();
  }, [messageId, source]);

  const analyzeUserAgents = (logs) => {
    let desktop = 0, mobile = 0, unknown = 0;
    let chrome = 0, safari = 0, firefox = 0, edge = 0, other = 0;

    logs.forEach(log => {
      if (!log.user_agent) {
        unknown++;
        return;
      }
      const ua = log.user_agent.toLowerCase();
      
      // Device
      if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone') || ua.includes('ipad')) {
        mobile++;
      } else if (ua.includes('windows') || ua.includes('macintosh') || ua.includes('linux')) {
        desktop++;
      } else {
        unknown++;
      }

      // Browser
      if (ua.includes('edge') || ua.includes('edg/')) {
        edge++;
      } else if (ua.includes('chrome')) {
        chrome++;
      } else if (ua.includes('safari') && !ua.includes('chrome')) {
        safari++;
      } else if (ua.includes('firefox')) {
        firefox++;
      } else {
        other++;
      }
    });

    setDeviceStats({ desktop, mobile, unknown });
    setBrowserStats({ chrome, safari, firefox, edge, other });
  };

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "NULL" || dateStr === "null") return "-";
    const str = String(dateStr).trim();
    if (str === "" || str === "0000-00-00 00:00:00" || str === "0000-00-00") return "-";

    // If already in DD-MM-YYYY HH:mm:ss format
    if (/^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/.test(str)) return str;
    // If already in DD-MM-YYYY format
    if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str;

    // If in YYYY-MM-DD HH:mm:ss format
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(str)) {
      const [datePart, timePart] = str.split(" ");
      const [year, month, day] = datePart.split("-");
      return `${day}-${month}-${year} ${timePart}`;
    }
    // If in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      const [year, month, day] = str.split("-");
      return `${day}-${month}-${year}`;
    }

    try {
      const d = new Date(str);
      if (isNaN(d.getTime())) return str;
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, "0");
      const min = String(d.getMinutes()).padStart(2, "0");
      const ss = String(d.getSeconds()).padStart(2, "0");
      return `${dd}-${mm}-${yyyy} ${hh}:${min}:${ss}`;
    } catch {
      return str;
    }
  };

  // Chart Data
  const eventBarData = {
    labels: ['Sends', 'Deliveries', 'Opens', 'Clicks', 'Bounces'],
    datasets: [{
      label: 'Event Count',
      data: [metrics.sends, metrics.deliveries, metrics.opens, metrics.clicks, metrics.bounces],
      backgroundColor: ['#8753de', '#00acac', '#348fe2', '#f59c1a', '#ff5b57'],
      borderWidth: 1,
      borderRadius: 4
    }]
  };

  const deviceDoughnutData = {
    labels: ['Desktop', 'Mobile', 'Unknown'],
    datasets: [{
      data: [deviceStats.desktop, deviceStats.mobile, deviceStats.unknown],
      backgroundColor: ['#348fe2', '#f59c1a', '#b6c2c9'],
      borderWidth: 0,
      hoverOffset: 4
    }]
  };

  const browserDoughnutData = {
    labels: ['Chrome', 'Safari', 'Firefox', 'Edge', 'Other'],
    datasets: [{
      data: [browserStats.chrome, browserStats.safari, browserStats.firefox, browserStats.edge, browserStats.other],
      backgroundColor: ['#f59c1a', '#348fe2', '#ff5b57', '#00acac', '#b6c2c9'],
      borderWidth: 0,
      hoverOffset: 4
    }]
  };

  const chronologicalData = [...data].reverse();
  const timelineChartData = {
    labels: chronologicalData.map(e => {
      const d = new Date(e.event_time);
      if (isNaN(d.getTime())) return "-";
      return `${d.getHours()}:${d.getMinutes().toString().padStart(2, '0')}`;
    }),
    datasets: [{
      label: 'Cumulative Events',
      data: chronologicalData.map((e, idx) => idx + 1),
      borderColor: '#8753de',
      backgroundColor: 'rgba(135, 83, 222, 0.1)',
      fill: true,
      tension: 0.4
    }]
  };

  const getEventIcon = (type) => {
    const t = (type || '').toUpperCase();
    if (t.includes('OPEN')) return { icon: faEnvelopeOpen, color: '#348fe2', bg: '#eef5fc' };
    if (t.includes('CLICK')) return { icon: faMousePointer, color: '#f59c1a', bg: '#fef5e8' };
    if (t.includes('DELIVER')) return { icon: faCheckCircle, color: '#00acac', bg: '#e5f6f6' };
    if (t.includes('BOUNCE')) return { icon: faExclamationTriangle, color: '#ff5b57', bg: '#ffeef0' };
    if (t.includes('SEND')) return { icon: faPaperPlane, color: '#8753de', bg: '#f4edfa' };
    return { icon: faInfoCircle, color: '#707478', bg: '#f1f2f3' };
  };

  const emailSubject = data.length > 0 ? data[0].subject : 'Email Details';
  const receiverEmail = data.length > 0 ? data[0].email : '-';
  const senderEmail = emailFrom && emailFrom !== "NULL" ? emailFrom : '-';

  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
      <div id="content" className="">
        
        {/* Header Section */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="page-header mb-1">
              Event Logs: <small>{emailSubject}</small>
            </h1>
            <p className="text-muted m-0" style={{ fontSize: '13px' }}>
              Sent from: <span className="text-primary fw-bold">{senderEmail}</span>
              <span style={{ margin: '0 8px' }}>|</span>
              Sent to: <span className="text-primary fw-bold">{receiverEmail}</span>
              {(attachmentName || attachmentLink) && (
                <span className="ms-3 badge bg-light text-dark border" style={{ marginLeft: '12px' }}>
                  <FontAwesomeIcon icon={faPaperclip} className="me-1 text-secondary" style={{ marginRight: '4px' }} />
                  {attachmentLink ? (
                    <a href={attachmentLink} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                      {attachmentName || "Attachment Link"}
                    </a>
                  ) : (
                    attachmentName
                  )}
                </span>
              )}
            </p>
          </div>
          <div className="d-flex align-items-center">
            <button 
              className="btn btn-primary btn-sm me-2" 
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              style={{ marginRight: '10px' }}
            >
              <FontAwesomeIcon icon={faDownload} className="me-2" />
              {downloadingPdf ? 'Generating PDF...' : 'Download'}
            </button>
            {htmlCode && (
              <div className="btn-group me-3" style={{ marginRight: '10px' }}>
                <button 
                  type="button" 
                  className={`btn btn-sm ${previewMode === 'desktop' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setPreviewMode(previewMode === 'desktop' ? null : 'desktop')}
                >
                  <i className="ti-desktop me-1"></i> Desktop Preview
                </button>
                <button 
                  type="button" 
                  className={`btn btn-sm ${previewMode === 'mobile' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setPreviewMode(previewMode === 'mobile' ? null : 'mobile')}
                >
                  <i className="ti-mobile me-1"></i> Mobile Preview
                </button>
              </div>
            )}
            <button className="btn btn-default btn-sm" onClick={() => navigate(-1)}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back to Statistics
            </button>
          </div>
        </div>
        
        {loading ? (
          <div className="row">
            <div className="col-lg-12 mb-4">
              <div className="panel panel-default with-rounded-corner with-shadow mb-4">
                <div className="panel-heading">
                  <h4 className="panel-title"><div className="skeleton-text" style={{ width: "150px" }}></div></h4>
                </div>
                <div className="panel-body p-4">
                  <div className="skeleton-text mb-3" style={{ height: "60px", borderRadius: "12px" }}></div>
                  <div className="skeleton-text mb-3" style={{ height: "60px", borderRadius: "12px" }}></div>
                  <div className="skeleton-text mb-3" style={{ height: "60px", borderRadius: "12px" }}></div>
                </div>
              </div>

              <div className="panel panel-default with-rounded-corner with-shadow">
                <div className="panel-heading">
                  <h4 className="panel-title"><div className="skeleton-text" style={{ width: "220px" }}></div></h4>
                </div>
                <div className="panel-body p-0">
                  <div className="skeleton-text" style={{ height: "500px", width: "100%", borderRadius: "0 0 4px 4px" }}></div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Standard Dashboard Widget Stats */}
            <div className="row">
              <div className="col-lg-12 mb-4">
                <div className="panel panel-default with-rounded-corner with-shadow h-100">
                  <div className="panel-heading px-3">
                    <h4 className="panel-title">Detailed Event Journey</h4>
                  </div>
                  <div className="panel-body px-3">
                    {data.length === 0 ? (
                      <div className="text-center py-5 text-muted">
                        <FontAwesomeIcon icon={faInfoCircle} size="3x" className="mb-3 opacity-50" />
                        <p>No events logged for this email yet.</p>
                      </div>
                    ) : (
                      <div className="timeline-container" style={{ position: 'relative', paddingLeft: '30px' }}>
                        {/* Vertical Line */}
                        <div style={{ position: 'absolute', top: '15px', bottom: '15px', left: '15px', width: '2px', background: '#e2e8f0', zIndex: 1 }}></div>
                        
                        {data.map((event, idx) => {
                          const evtConfig = getEventIcon(event.event_type);
                          return (
                            <div key={idx} className="timeline-item mb-4" style={{ position: 'relative', zIndex: 2 }}>
                              {/* Icon Marker */}
                              <div 
                                style={{ 
                                  position: 'absolute', left: '-30px', top: '0', width: '32px', height: '32px',
                                  borderRadius: '50%', background: evtConfig.bg, color: evtConfig.color,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  border: '2px solid #fff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                              >
                                <FontAwesomeIcon icon={evtConfig.icon} size="sm" />
                              </div>
                              
                              {/* Content Box */}
                              <div className="p-3" style={{ background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', marginLeft: '15px' }}>
                                <div className={`d-flex justify-content-between align-items-center px-2 ${(event.source_ip || event.user_agent || event.bounce_type || event.complaint_feedback_type || event.link) ? 'mb-2' : ''}`}>
                                  <span className="fw-bold" style={{ color: evtConfig.color, textTransform: 'uppercase', fontSize: '13px', letterSpacing: '0.5px' }}>
                                    {event.event_type}
                                  </span>
                                  <span className="text-muted" style={{ fontSize: '12px' }}>
                                    <FontAwesomeIcon icon={faClock} className="me-1" /> {formatDate(event.event_time)}
                                  </span>
                                </div>
                                
                                {(event.source_ip || event.user_agent || event.bounce_type || event.complaint_feedback_type || event.link) && (
                                  <div className="row">
                                    <div className="col-md-7">
                                      {event.source_ip && (
                                        <div className="mb-1" style={{ fontSize: '13px', color: '#475569' }}>
                                          <FontAwesomeIcon icon={faGlobe} className="text-muted me-2" style={{ width: '14px' }} /> 
                                          IP: {event.source_ip}
                                        </div>
                                      )}
                                      {event.user_agent && (
                                        <div className="mb-1" style={{ fontSize: '13px', color: '#475569' }}>
                                          <FontAwesomeIcon icon={faDesktop} className="text-muted me-2" style={{ width: '14px' }} />
                                          <span title={event.user_agent} style={{ display: 'inline-block', maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                                            {event.user_agent}
                                          </span>
                                        </div>
                                      )}
                                      {event.link && event.link !== "NULL" && (
                                        <div className="mb-1" style={{ fontSize: '13px', color: '#475569' }}>
                                          <FontAwesomeIcon icon={faMousePointer} className="text-muted me-2" style={{ width: '14px' }} />
                                          <a href={event.link} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                                            {event.link}
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                    <div className="col-md-5">
                                      {event.bounce_type && (
                                        <div className="p-2" style={{ background: '#fef2f2', borderLeft: '3px solid #ef4444', borderRadius: '4px', fontSize: '12px' }}>
                                          <strong>Bounce:</strong> {event.bounce_type} <br/>
                                          <span className="text-muted">{event.bounce_sub_type}</span>
                                        </div>
                                      )}
                                      {event.complaint_feedback_type && (
                                        <div className="p-2 mt-1" style={{ background: '#fffbeb', borderLeft: '3px solid #f59e0b', borderRadius: '4px', fontSize: '12px' }}>
                                          <strong>Complaint:</strong> {event.complaint_feedback_type}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Attachment Section */}
            {(attachmentName || attachmentLink) && (
              <div className="row mt-4">
                <div className="col-md-12">
                  <div className="panel panel-default with-rounded-corner with-shadow" style={{ borderLeft: '4px solid #348fe2' }}>
                    <div className="panel-heading d-flex justify-content-between align-items-center">
                      <h4 className="panel-title mb-0">
                        <FontAwesomeIcon icon={faPaperclip} className="me-2 text-primary" style={{ marginRight: '8px' }} />
                        Email Attachment Details
                      </h4>
                    </div>
                    <div className="panel-body p-3" style={{ background: '#f8fafc' }}>
                      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div className="d-flex align-items-center">
                          <div 
                            className="d-flex align-items-center justify-content-center me-3"
                            style={{
                              width: '42px',
                              height: '42px',
                              borderRadius: '8px',
                              backgroundColor: '#eef5fc',
                              color: '#348fe2',
                              fontSize: '18px',
                              marginRight: '12px'
                            }}
                          >
                            <FontAwesomeIcon icon={faPaperclip} />
                          </div>
                          <div>
                            <div className="fw-bold text-dark" style={{ fontSize: '14px' }}>
                              {attachmentName || "Attached File"}
                            </div>
                            {attachmentLink && (
                              <small className="text-muted text-break" style={{ fontSize: '12px' }}>
                                {attachmentLink}
                              </small>
                            )}
                          </div>
                        </div>
                        {attachmentLink ? (
                          <a 
                            href={attachmentLink} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="btn btn-primary btn-sm px-3"
                            style={{ borderRadius: '6px' }}
                          >
                            <FontAwesomeIcon icon={faDownload} className="me-2" style={{ marginRight: '6px' }} />
                            View / Download Attachment
                          </a>
                        ) : (
                          <span className="badge bg-secondary">No Direct Link</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Preview Section */}
            {previewMode && htmlCode && (
              <div className="row mt-4">
                <div className="col-md-12">
                  <div className="panel panel-default with-rounded-corner with-shadow">
                    <div className="panel-heading">
                      <h4 className="panel-title">Email Template Preview ({previewMode === 'mobile' ? 'Mobile' : 'Desktop'})</h4>
                    </div>
                    <div className="panel-body d-flex justify-content-center p-4" style={{ backgroundColor: '#f9f9f9', minHeight: '60vh' }}>
                      <iframe
                        title="HTML Preview"
                        srcDoc={(() => {
                          let friendlyHtml = htmlCode || "";
                          friendlyHtml = friendlyHtml.replace(/\$userinfo->name/g, "Admin Name");
                          friendlyHtml = friendlyHtml.replace(/\$subscriber->name/g, "Client Name");
                          friendlyHtml = friendlyHtml.replace(/\$subscriber->email/g, "client@example.com");
                          friendlyHtml = friendlyHtml.replace(/\$subscriber->mobile/g, "9876543210");
                          friendlyHtml = friendlyHtml.replace(/\$[a-zA-Z0-9_]+->([a-zA-Z0-9_]+)/g, (match, fieldName) => {
                            const readableName = fieldName.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                            return `[${readableName}]`;
                          });
                          return friendlyHtml;
                        })()}
                        style={{
                          width: previewMode === 'desktop' ? '100%' : '375px',
                          height: '60vh',
                          border: '1px solid #ddd',
                          backgroundColor: "#fff",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          borderRadius: previewMode === 'mobile' ? "20px" : "4px",
                          transition: "width 0.3s ease, border-radius 0.3s ease"
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EmailEventLogsReport;

